import { type VariantProps, cva } from 'class-variance-authority'
import { SendHorizontalIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'

export interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onSubmit'>,
    Omit<VariantProps<typeof textareaVariants>, 'disabled'> {
  state?: 'default' | 'error'
  resizable?: boolean
  autoGrow?: boolean
  minRows?: number
  maxRows?: number
  onSubmit?: (value: string) => void
}

const textareaVariants = cva(
  'flex min-h-24 w-full rounded border bg-main-background-card text-body-default text-main-foreground-primary p-2 outline-none ring-offset-main-background-card transition-colors placeholder:text-main-foreground-placeholder',
  {
    variants: {
      state: {
        default:
          'border-main-border-input hover:border-main-border-bold focus:border-main-border-focus focus:ring-offset focus:ring-main-border-focus',
        error:
          'border-main-border-error focus:border-main-border-error focus:ring-offset focus:ring-main-border-error',
      },
      resizable: {
        true: 'resize-y',
        false: 'resize-none',
      },
      disabled: {
        true: 'border border-main-border-default hover:border-main-border-default bg-main-background-interactive-secondary-disabled text-main-foreground-disabled cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      state: 'default',
      resizable: true,
      disabled: false,
    },
  },
)

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      state = 'default',
      resizable = true,
      autoGrow = false,
      minRows,
      maxRows,
      rows,
      disabled,
      onSubmit,
      ...props
    },
    ref,
  ) => {
    const hasError = state === 'error'
    const isControlled = props.value !== undefined
    const manageValue = !!onSubmit || isControlled
    const [managedValue, setManagedValue] = React.useState(() =>
      isControlled ? '' : String(props.defaultValue ?? ''),
    )
    const currentValue = isControlled ? String(props.value) : manageValue ? managedValue : undefined

    const innerRef = React.useRef<HTMLTextAreaElement | null>(null)
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ;(ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
        }
      },
      [ref],
    )

    const resize = React.useCallback(() => {
      const el = innerRef.current
      if (!el || !autoGrow) return
      const computed = window.getComputedStyle(el)
      const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2
      const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom)
      const borderY = parseFloat(computed.borderTopWidth) + parseFloat(computed.borderBottomWidth)
      const minHeight = minRows ? minRows * lineHeight + paddingY + borderY : 0
      const maxHeight = maxRows ? maxRows * lineHeight + paddingY + borderY : Infinity

      el.style.height = 'auto'
      const contentHeight = el.scrollHeight + borderY
      el.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`
      el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden'
    }, [autoGrow, minRows, maxRows])

    React.useLayoutEffect(() => {
      resize()
    }, [resize, currentValue, props.value, props.defaultValue])

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled && manageValue) {
        setManagedValue(event.target.value)
      }
      props.onChange?.(event)
      resize()
    }

    const handleSubmit = () => {
      onSubmit?.(String(currentValue ?? ''))
      if (!isControlled && manageValue) {
        setManagedValue('')
      }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Submit on Cmd/Ctrl + Enter when onSubmit is provided and there is content
      if (
        onSubmit &&
        (event.key === 'Enter' || event.code === 'Enter') &&
        (event.metaKey || event.ctrlKey)
      ) {
        if (String(currentValue ?? '').length > 0) {
          event.preventDefault()
          handleSubmit()
        } else {
          event.preventDefault()
        }
      }
      props.onKeyDown?.(event)
    }

    return (
      <div className={cn('relative flex w-full overflow-clip', { 'opacity-50': disabled })}>
        <textarea
          ref={setRefs}
          aria-invalid={hasError}
          disabled={disabled}
          rows={autoGrow ? minRows : rows}
          className={cn(
            textareaVariants({
              state: hasError ? 'error' : 'default',
              resizable: autoGrow ? false : resizable,
              disabled: !!disabled,
            }),
            autoGrow && 'min-h-0',
            className,
          )}
          {...props}
          {...(manageValue
            ? { value: currentValue as string }
            : { defaultValue: props.defaultValue })}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {onSubmit && (
          <Button
            disabled={(currentValue ?? '').length === 0}
            onClick={handleSubmit}
            size='icon'
            className='absolute bottom-2 right-2'
          >
            <SendHorizontalIcon className='size-4' />
          </Button>
        )}
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'

export { Textarea }
